import React, { useEffect, useState } from "react";
import {
  vehicleFileAPI,
  VehicleFileModel,
  VehicleFileViewModel,
} from "../api/vehicleFileAPI";
import { MakeModel, makeAPI } from "../api/makeAPI";
import {
  Button,
  Form,
  Card,
  InputGroup,
  Container,
  ButtonToolbar,
  ButtonGroup,
  Modal,
} from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Problem } from "../api/backendApi";
import { useRoutablePages } from "../common/RoutablePages";
import VehicleFileDetailsForm, { VehicleFileDetails } from "./VehicleFileForm";
import { Search } from "react-bootstrap-icons";

function VehicleFilesPage() {
  const [files, setFiles] = useState<VehicleFileViewModel[]>([]);
  const [makes, setMakes] = useState<MakeModel[]>([]);
  const [vehicleFilesDescriptionFilter, setVehicleFilesDescriptionFilter] =
    useState<string>("");
  const [selectedMakeCode, setSelectedMakeCode] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const routes = useRoutablePages();
  const params = useParams();
  const name = params.name ?? "";
  const [filteredVehicleFiles, setFilteredVehicleFiles] = useState<
    VehicleFileViewModel[]
  >([]);
  const [newVehicleFile, setNewVehicleFile] = useState<VehicleFileDetails>({});
  const [createVehicleFileError, setCreateVehicleFileError] = useState<
    Problem | undefined
  >();

  useEffect(() => {
    if (name !== "") {
      setSelectedMakeCode(name);
    } else {
      setSelectedMakeCode("");
      setFiles([]);
    }
  }, [name]);

  useEffect(() => {
    async function loadVehicleFiles(makeCode: string) {
      setLoading(true);
      const data = await vehicleFileAPI.getVehicleFilesByMake(makeCode);

      if (data.ok) {
        setError(undefined);
        setFiles(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    if (selectedMakeCode) {
      loadVehicleFiles(selectedMakeCode);
      setVehicleFilesDescriptionFilter("");
    }
  }, [selectedMakeCode]);

  useEffect(() => {
    async function loadMakes() {
      setLoading(true);
      const data = await makeAPI.getMakes();

      if (data.ok) {
        setError(undefined);
        setMakes(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadMakes();
  }, []);

  const filterVehicleFiles = () => {
    var filteredVehicleFiles = [...files];
    filteredVehicleFiles = filteredVehicleFiles.filter(
      (vehicleFile) =>
        vehicleFile.description
          ?.toUpperCase()
          .includes(vehicleFilesDescriptionFilter.toUpperCase()) ||
        vehicleFile.number
          ?.toUpperCase()
          .includes(vehicleFilesDescriptionFilter.toUpperCase()),
    );

    setFilteredVehicleFiles(filteredVehicleFiles);
  };

  useEffect(() => {
    filterVehicleFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFilesDescriptionFilter, files]);

  const createNewFile = async () => {
    if (!selectedMakeCode || !newVehicleFile) return;

    let response = await vehicleFileAPI.createNewVehicleFile(selectedMakeCode, {
      description: newVehicleFile.description ?? "",
      type: newVehicleFile.type,
      rsa: false,
    } as VehicleFileModel);

    if (response.ok) {
      routes.toVehicleFileDetails(response.value);
    } else {
      setCreateVehicleFileError(response.problem);
    }
  };

  return (
    <>
      <div className="main-container-title">
        <h1>Vehicle Files</h1>

        <ErrorDisplay error={error} />

        <LoadingDisplay loading={loading} />

        {!loading && !error && (
          <ButtonToolbar>
            <InputGroup>
              <Form.Select
                id="toolbarMakes"
                value={selectedMakeCode}
                onChange={(e) => {
                  setSelectedMakeCode(e.target.value);
                  routes.toVehiclesByMake(e.target.value);
                }}
              >
                {!selectedMakeCode && (
                  <option key="" value="">
                    [Select Make]
                  </option>
                )}
                {makes.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.name} [{m.code}]
                  </option>
                ))}
              </Form.Select>
              {selectedMakeCode && (
                <span
                  className="input-group-text"
                  style={{
                    display: "flex",
                    marginLeft: "1rem",
                    flexDirection: "row",
                  }}
                >
                  <Search width={12} height={12} />
                  <Form.Label
                    style={{
                      fontWeight: "400",
                      fontSize: "16px",

                      display: "flex",
                      marginTop: "auto",
                      marginBottom: "auto",
                      marginLeft: "5px",
                    }}
                  >
                    Filter
                  </Form.Label>
                </span>
              )}
              {selectedMakeCode && (
                <Form.Control
                  size="sm"
                  onChange={(e) => {
                    setVehicleFilesDescriptionFilter(e.target.value);
                  }}
                  style={{
                    height: "50p",
                    width: "400px",
                  }}
                ></Form.Control>
              )}
            </InputGroup>
            <ButtonGroup className="ms-auto">
              <Button
                type="button"
                disabled={!selectedMakeCode}
                onClick={() => {
                  setNewVehicleFile({});
                  setShowCreateModal(true);
                }}
              >
                <span className="icon-home"></span>Create new Vehicle File
              </Button>
            </ButtonGroup>
          </ButtonToolbar>
        )}
      </div>
      <div className="main-container-content">
        {!loading && (
          <Container className="overflow-auto">
            {filteredVehicleFiles.map((file) => (
              <Card
                key={file.number}
                className="card small"
                style={{ marginTop: "1rem" }}
              >
                <Card.Header className="section dark">
                  {file.number}
                </Card.Header>
                <Card.Body>
                  <Card.Text className="section">
                    <b>{file.make}</b>
                    <br />
                    {file.description}
                    {file.checkedOutBy && (
                      <>
                        <br />
                        Checked out by {file.checkedOutBy.name}
                      </>
                    )}
                    <Link
                      to={routes.getVehicleFileDetails(file.number)}
                      className="stretched-link"
                    />
                  </Card.Text>
                </Card.Body>
              </Card>
            ))}
          </Container>
        )}
      </div>
      <Modal
        size="xl"
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create new vehicle file</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ErrorDisplay error={createVehicleFileError} />
          <Form>
            <VehicleFileDetailsForm
              details={newVehicleFile}
              onChange={(details) => setNewVehicleFile(details)}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => createNewFile()}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default VehicleFilesPage;
